import { Component, type ErrorInfo, type ReactNode } from 'react'

/** Props for {@link WebGLErrorBoundary}. */
export interface WebGLErrorBoundaryProps {
  /** Rendered instead of `children` after an error. Default: nothing. */
  fallback?: ReactNode
  /** Used in the console message, e.g. the scene id. */
  label?: string
  /** Called once per caught error, so the owner can show a still and schedule a retry. */
  onError?: (error: Error) => void
  children: ReactNode
}

interface State {
  failed: boolean
}

/**
 * Catches errors thrown while rendering a 3D scene (shader compile errors, failed loads,
 * context creation) and shows `fallback`, so the rest of the page keeps working.
 * React still logs the error to the console, so `scripts/snap.mjs` reports it.
 * `SceneCanvas` remounts it with a new `key` to retry.
 */
export class WebGLErrorBoundary extends Component<WebGLErrorBoundaryProps, State> {
  state: State = { failed: false }

  static getDerivedStateFromError(): State {
    return { failed: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(
      `[vokarta] scene "${this.props.label ?? 'unknown'}" failed`,
      error,
      info.componentStack,
    )
    this.props.onError?.(error)
  }

  render(): ReactNode {
    return this.state.failed ? (this.props.fallback ?? null) : this.props.children
  }
}
